import React, { useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QrCode, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function QrVerify() {
  const { company } = useAuth();
  const currency = company?.currency || "FCFA";
  const [payload, setPayload] = useState("");
  const [result, setResult] = useState(null);

  const verify = async (e) => {
    e?.preventDefault();
    try {
      const { data } = await api.post("/qr/verify", { payload });
      setResult(data);
    } catch (err) {
      setResult({ valid: false, error: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Vérification QR Code</h1>
        <p className="text-sm text-slate-500">Scannez ou collez le contenu du QR</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><QrCode className="w-5 h-5 text-orange-500" /> Saisir le code</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <form onSubmit={verify}>
            <Label>Contenu du QR (ex: BT-2026-000001|xxxx)</Label>
            <Input value={payload} onChange={(e) => setPayload(e.target.value)} data-testid="qr-payload-input" placeholder="BT-2026-000001|abc123..." />
            <Button type="submit" className="btn-pill bg-orange-500 hover:bg-orange-600 w-full mt-3" data-testid="qr-verify-btn">Vérifier</Button>
          </form>
        </CardContent>
      </Card>

      {result && (
        <Card data-testid="qr-result">
          <CardContent className="p-6">
            {result.error ? (
              <div className="flex items-center gap-3 text-red-600">
                <XCircle className="w-8 h-8" />
                <div className="font-semibold">{result.error}</div>
              </div>
            ) : result.valid ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-emerald-600">
                  <CheckCircle2 className="w-8 h-8" />
                  <div>
                    <div className="font-display font-bold text-xl">BILLET VALIDE</div>
                    <div className="text-xs text-slate-500">Marqué comme utilisé</div>
                  </div>
                </div>
                <TicketDetails t={result.ticket} currency={currency} />
              </div>
            ) : result.cancelled ? (
              <div className="flex items-center gap-3 text-red-600"><XCircle className="w-8 h-8" /> <div className="font-semibold">Billet annulé</div></div>
            ) : result.already_used ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-amber-600">
                  <AlertTriangle className="w-8 h-8" />
                  <div className="font-display font-bold text-xl">BILLET DÉJÀ UTILISÉ</div>
                </div>
                <TicketDetails t={result.ticket} currency={currency} />
              </div>
            ) : (
              <div className="text-red-600">Billet non valide</div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TicketDetails({ t, currency }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-sm">
      <Row k="Numéro" v={t.ticket_number} />
      <Row k="Trajet" v={`${t.from_city} → ${t.to_city}`} />
      <Row k="Date" v={`${t.date} ${t.schedule}`} />
      <Row k="Client" v={`${t.customer_firstname} ${t.customer_name}`} />
      <Row k="Téléphone" v={t.customer_phone} />
      <Row k="Total" v={`${(t.total || 0).toLocaleString("fr-FR")} ${currency}`} />
      <Row k="Statut" v={t.status} />
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-900/60">
      <div className="text-xs text-slate-500 uppercase tracking-wider">{k}</div>
      <div className="font-semibold text-sm">{v}</div>
    </div>
  );
}
